import React, { useEffect } from "react";
import Bouton from "../../common/HorsConnexion/Bouton";
import CasesCode from "../../common/HorsConnexion/CasesCode";
import Compte from "../../common/HorsConnexion/Compte";
import { formaterDuree, formaterValidite } from "../../common/HorsConnexion/horsConnexion";
import Icone from "../../common/HorsConnexion/Icone";
import MessageEtat from "../../common/HorsConnexion/MessageEtat";
import PiedCarte from "../../common/HorsConnexion/PiedCarte";

export const LONGUEUR_CODE = 6;

/**
 * Vue « code de vérification » (MFA par e-mail) de la connexion.
 * `defi` = { challenge, emailHint, expiresIn } renvoyé par l'API ;
 * `erreurs.code` (+ nbAttempts) signale un code refusé.
 */
const CodeVerification = ({
  defi,
  code,
  onCode,
  onSubmit,
  erreurs = {},
  isLoading = false,
  secondesValidite = 0,
  secondesAvantRenvoi = 0,
  onRenvoyer,
  onRecommencer,
}) => {
  const expire = secondesValidite <= 0;
  const nbEssais = Number(erreurs.nbAttempts) || 0;
  let erreurCode = null;
  if (erreurs.code) {
    erreurCode =
      nbEssais > 0 ? (
        <>
          Code incorrect. Il vous reste <strong>{nbEssais}</strong> {nbEssais > 1 ? "essais" : "essai"}.
        </>
      ) : (
        erreurs.code
      );
  }
  const autre = !erreurs.code && !erreurs.restart && erreurs.other ? erreurs.other : null;

  // Code refusé : les cases sont vidées ; le curseur revient dans la première
  // dès qu'elles sont de nouveau saisissables.
  useEffect(() => {
    if (erreurs.code && !isLoading && !code) {
      const premiere = document.getElementById("code-1");
      if (premiere) premiere.focus();
    }
  }, [erreurs.code, isLoading, code]);

  return (
    <section aria-labelledby="titre-mfa">
      <button type="button" className="lien retour" onClick={onRecommencer}>
        <Icone nom="chevronGauche" epaisseur={2.2} />
        Recommencer la connexion
      </button>
      <h2 id="titre-mfa">Code de vérification</h2>
      <p className="sous-titre">
        Vous vous connectez depuis l'extérieur du réseau CAF. Un code à six chiffres vient d'être
        envoyé à l'adresse de votre compte ; il est vérifié dès que vous l'avez saisi en entier.
      </p>
      {defi.emailHint && <Compte email={defi.emailHint} icone="enveloppe" />}

      <form onSubmit={onSubmit} noValidate>
        <div className={"champ" + (erreurCode ? " erreur" : "")}>
          <label htmlFor="code-1">Saisissez le code reçu</label>
          <CasesCode
            value={code}
            onChange={onCode}
            longueur={LONGUEUR_CODE}
            erreur={!!erreurCode}
            disabled={isLoading}
            autoFocus
          />
          {erreurCode && (
            <p className="aide-erreur centree" role="alert">
              {erreurCode}
            </p>
          )}
          <div className={"validite" + (expire ? " fin" : "")} aria-live="off">
            <Icone nom="horloge" epaisseur={2.2} />
            {expire ? (
              <span role="status">Ce code a expiré. Demandez-en un nouveau.</span>
            ) : (
              <span>
                Code valable encore <b>{formaterDuree(secondesValidite)}</b>
              </span>
            )}
          </div>
        </div>

        {autre && (
          <MessageEtat ton="avert" icone="alerte">
            {autre}
          </MessageEtat>
        )}

        <Bouton type="submit" charge={isLoading} disabled={code.length !== LONGUEUR_CODE || expire}>
          {isLoading ? "Vérification…" : "Valider le code"}
        </Bouton>

        <div className="actions-mfa">
          <button
            type="button"
            className="lien"
            disabled={secondesAvantRenvoi > 0 || isLoading}
            onClick={onRenvoyer}
          >
            {secondesAvantRenvoi > 0 ? `Renvoyer le code (${secondesAvantRenvoi} s)` : "Renvoyer le code"}
          </button>
          <span className="mention">Vous ne recevez rien ? Vérifiez vos indésirables.</span>
        </div>
      </form>

      <PiedCarte>Code à usage unique, valable {formaterValidite(defi.expiresIn || 300)}.</PiedCarte>
    </section>
  );
};

export default CodeVerification;
